import React, { useCallback } from "react";
import { Form, Image } from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import { useFormik } from "formik";
import {
  initialValues,
  validationSchema,
  accountOptions,
} from "./PlanesForm.form";
import { PlanesAPI } from "../../../../api";
import { useAuth } from "../../../../hooks";
import "./PlanesForm.scss";

const planesController = new PlanesAPI();

export function PlanesForm(props) {
  const { onClose, onReload, plan, idPermission } = props;
  const { accessToken, user } = useAuth();

  const formik = useFormik({
    initialValues: initialValues(plan),
    validationSchema: validationSchema(),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        if (!plan) {
          // CREAR
          await planesController.createPlan(
            accessToken,
            idPermission,
            formValue,
          );
        } else {
          // ACTUALIZAR

          await planesController.updatePlan(
            accessToken,
            user.user._id,
            plan._id,
            formValue,
          );
        }

        onReload();
        onClose();
      } catch (error) {
        console.error("Error al procesar el plan:", error);
      }
    },
  });

  // Manejo de Dropzone para la miniatura
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const validMimeTypes = ["image/jpeg", "image/png"];
        if (validMimeTypes.includes(file.type)) {
          // Asignar URL local y objeto File
          formik.setFieldValue("miniature", URL.createObjectURL(file));
          formik.setFieldValue("file", file);
        } else {
          console.log("Tipo de archivo no soportado:", file.type);
        }
      }
    },
    [formik],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    onDrop,
    multiple: false,
  });

  /**
   * Determina qué miniatura mostrar:
   * - Si hay un archivo nuevo, usa la URL local creada con URL.createObjectURL(file).
   * - Si existe plan y tiene un miniature (URL en BD), la muestra del backend.
   */
  const getMiniature = () => {
    if (formik.values.file) {
      // Vista previa de la imagen recién cargada
      return formik.values.miniature;
    } else if (formik.values.miniature) {
      // Si estamos editando y ya hay una miniatura guardada en BD
      return formik.values.miniature;
    }
    return null;
  };

  return (
    <Form className="plan-form" onSubmit={formik.handleSubmit}>
      <div className="plan-form__miniature" {...getRootProps()}>
        <input {...getInputProps()} />
        {getMiniature() ? (
          <Image size="small" src={getMiniature()} />
        ) : (
          <div>
            <span>Arrastra o haz clic para subir la miniatura</span>
          </div>
        )}
      </div>

      {/* Campo: Título */}
      <Form.Input
        label="Título del Plan"
        name="title"
        placeholder="Ej. Plan Premium"
        onChange={formik.handleChange}
        value={formik.values.title}
        error={formik.errors.title}
      />

      {/* Campo: Descripción */}
      <Form.TextArea
        label="Descripción del Plan"
        name="description"
        placeholder="Ej. Descripción detallada de lo que incluye"
        onChange={formik.handleChange}
        value={formik.values.description}
        error={formik.errors.description}
      />
      {/* Account */}
      <Form.Select
        label="Tipo de Cuenta"
        name="account"
        options={accountOptions}
        placeholder="Selecciona el tipo de cuenta"
        onChange={(_, data) => formik.setFieldValue("account", data.value)}
        value={formik.values.account}
        error={formik.errors.account ? true : false}
      />

      <Form.Group widths="equal">
        {/* Campo: Nº de Visores VR */}
        <Form.Input
          label="Nº de Visores VR"
          type="number"
          name="vrHeadsetNumber"
          placeholder="Ej. 5"
          onChange={formik.handleChange}
          value={formik.values.vrHeadsetNumber}
          error={formik.errors.vrHeadsetNumber}
        />
        {/* Campo: Modelo de Visor VR */}
        <Form.Input
          label="Modelo de Visor VR"
          name="vrHeadsetModel"
          placeholder="Ej. Oculus Quest 2"
          onChange={formik.handleChange}
          value={formik.values.vrHeadsetModel}
          error={formik.errors.vrHeadsetModel}
        />
      </Form.Group>

      <Form.Group widths="equal">
        {/* Campo: Modelo de Maleta/Mochila */}
        <Form.Input
          label="Modelo de maleta/mochila"
          name="bagModel"
          placeholder="Ej. Mochila VR Deluxe"
          onChange={formik.handleChange}
          value={formik.values.bagModel}
          error={formik.errors.bagModel}
        />
        {/* Campo: Meses de licencia */}
        <Form.Input
          label="Meses de licencia"
          type="number"
          name="accessTime"
          placeholder="Ej. 12"
          onChange={formik.handleChange}
          value={formik.values.accessTime}
          error={formik.errors.accessTime}
        />
      </Form.Group>

      <Form.Group widths="equal">
        {/* Campo: Nº de profesores */}
        <Form.Input
          label="Nº de profesores"
          type="number"
          name="teacherNumber"
          placeholder="Ej. 3"
          onChange={formik.handleChange}
          value={formik.values.teacherNumber}
          error={formik.errors.teacherNumber}
        />
        {/* Campo: Nº de alumnos */}
        <Form.Input
          label="Nº de alumnos"
          type="number"
          name="studentsNumber"
          placeholder="Ej. 3"
          onChange={formik.handleChange}
          value={formik.values.studentsNumber}
          error={formik.errors.studentsNumber}
        />
      </Form.Group>
      <Form.Group widths="equal">
        {/* Campo: Educamaker */}
        <Form.Checkbox
          label="Educamaker"
          name="educamaker"
          onChange={(_, data) =>
            formik.setFieldValue("educamaker", data.checked)
          }
          checked={formik.values.educamaker}
          error={formik.errors.educamaker}
        />
        {/* Campo: Soporte */}
        <Form.Checkbox
          label="Formación de iniciación"
          name="curseBasic"
          onChange={(_, data) =>
            formik.setFieldValue("curseBasic", data.checked)
          }
          checked={formik.values.curseBasic}
          error={formik.errors.curseBasic}
        />

        {/* Campo: Publicidad */}
        <Form.Checkbox
          label="Formación de certificación"
          name="curseCertification"
          onChange={(_, data) =>
            formik.setFieldValue("curseCertification", data.checked)
          }
          checked={formik.values.curseCertification}
          error={formik.errors.curseCertification}
        />
        {/* Campo: Soporte */}
        <Form.Checkbox
          label="Soporte"
          name="support"
          onChange={(_, data) => formik.setFieldValue("support", data.checked)}
          checked={formik.values.support}
          error={formik.errors.support}
        />

        {/* Campo: Publicidad */}
        <Form.Checkbox
          label="Publicidad"
          name="publicity"
          onChange={(_, data) =>
            formik.setFieldValue("publicity", data.checked)
          }
          checked={formik.values.publicity}
          error={formik.errors.publicity}
        />
      </Form.Group>
      {/* Campo: Nº de profesores */}
      <Form.Input
        label="Precio del Plan"
        type="number"
        name="price"
        placeholder="Ej. 299"
        onChange={formik.handleChange}
        value={formik.values.price}
        error={formik.errors.price}
      />
      <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
        {!plan ? "Crear Plan" : "Actualizar Plan"}
      </Form.Button>
    </Form>
  );
}
